import React, {
  useCallback,
  useMemo,
  ChangeEvent,
  useEffect,
  memo,
}                 from 'react';
import styled     from 'styled-components';
import {
  Form,
  Field,
  FieldArray,
  useFormikContext,
}                 from 'formik';
import {
  withWidth,
  WithWidth,
}                 from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import {
  IInvoiceDetails,
  ISoldLot,
  IInvoiceFee,
  TInvoiceValues,
}                 from '@typings/invoiceManagement';
import {
  EInvoiceStatus,
  EPaymentStatus,
  EFeeType,
}                 from '@enums/invoiceManagement';
import {
  formatCurrency,
  formatParsedZoneDate,
}                 from '@utils/commonFunctions';
import {
  invoiceStatusOptions,
  paymentStatusOptions,
  PRICING_LABELS,
  FEE_PRICING_KEYS,
}                 from '@modules/invoiceManagement/utils/suggestions';
import {
  Button,
  Select,
  CurrencyInput,
}                 from '@components/index';
import { WithoutWidthButton } from '@components/Button/WithoutWidthButton';

interface IStyledProps {
  width?: number;
}

const StyledForm = styled(Form)`
  && {
    h2 {
      margin-top: 30px;
    }
  }
`;

const LineWrapper = styled.div<{ width?: string; firstSpanWidth?: string; color?: string }>`
  display       : flex;
  align-items   : center;
  width         : ${({ width }) => width || '800px'};
  padding       : 5px;
  margin-bottom : 5px;

  span {
    color     : ${({ color, theme: { textColor } }): string => color || textColor.black};
    font-size : 16px;
  }

  > span:first-child {
    width       : ${({ firstSpanWidth }) => firstSpanWidth ?? '125px'};
    color       : ${({ theme: { textColor } }): string => textColor.black};
    text-align  : left;
    white-space : nowrap;
  }

  > div:last-child,
  > span:last-child {
    flex-basis  : 220px;
    margin-left : 15px;
  }

  hr {
    width   : 1000px;
    size    : 1px;
    display : block;
    color   : ${({ theme: { textColor } }): string => textColor.gray};
  }
`;

const FeeDescription = styled.div`
  font-size : 14px;
  color     : ${({ theme: { textColor } }): string => textColor.gray};
`;

const InfoRow = styled.div`
  display     : flex;
  align-items : center;

  span {
    font-size: 16px;
  }
`;

const Header = styled(InfoRow)`
  margin-top: 15px;

  span {
    font-size   : 20px;
    font-weight : 500;
  }
`;

const Footer = styled(InfoRow)`
  margin-top: 20px;
`;

const DescriptionColumn = styled.div`
  flex-basis: 360px;
`;

const QtyColumn = styled.div<IStyledProps>`
  text-align   : right;
  flex-basis   : 90px;
  margin-right : 30px;

  & div {
    width: ${({ width }): string | false => !!width && `${width}%`};
  }

  @media (min-width: 1280px) {
    margin-right : 20px;
  }
`;

const PriceColumn = styled.div`
  margin-right : 20px;
  text-align   : right;
  flex-basis   : 120px;
`;

const InfoColumn = styled.div`
  text-align : center;
  flex-basis : 220px;
`;

const TotalColumn = styled.div`
  text-align : right;
  flex-basis : 140px;
`;

const TotalCount = styled.div`
  text-align : right;
  font-size  : 16px;
  width      : 125px;
`;

const ViewButtonWrap = styled.div`
  flex-basis: 205px;

  @media (min-width: 1280px) {
    flex-basis: 110px;
  }
`;

const getUnpaidFeeValue = (fee?: IInvoiceFee) => {
  if (!fee) return 0;

  switch (fee.type) {
    case EFeeType.NadaBookOutFee:
      return fee.status !== EPaymentStatus.Paid
        ? (fee.value * fee.quantityUsage)
        : 0;

    case EFeeType.NadaMonthlyFee:
      return fee.status !== EPaymentStatus.Paid
        ? fee.value
        : 0;

    default:
      return fee.status === EPaymentStatus.Unpaid
        ? fee.value
        : 0;
  }
};

const TAXABLE_FEE_TYPES: EFeeType[] = [];

interface IProps {
  invoiceData              : IInvoiceDetails;
  handleDeleteSoldLot      : (soldLotId: string) => void;
  handleResetFee           : (feeType: EFeeType) => void;
  onChangeInvoiceStatus    : (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeInvoiceFeeStatus : (event: ChangeEvent<HTMLInputElement>, feeType: EFeeType) => void;
  onChangeSoldLotStatus    : (event: ChangeEvent<HTMLInputElement>, lotId: string) => void;
  openNadaUsageModal       : () => Promise<void>;
}

const InvoiceDetails: React.FC<IProps & WithWidth> = ({
  invoiceData,
  handleDeleteSoldLot,
  width,
  handleResetFee,
  onChangeInvoiceStatus,
  onChangeInvoiceFeeStatus,
  onChangeSoldLotStatus,
  openNadaUsageModal,
}) => {
  const {
    dirty,
    values,
    isValid,
    setValues,
    isSubmitting,
  } = useFormikContext<TInvoiceValues>();

  const isSmallScreen = useMemo(
    () => ['xs', 'sm'].includes(width),
    [width],
  );

  const getFormatCurrency = useCallback(
    (
      status         : EPaymentStatus,
      feeValue       : number,
      quantityUsage? : number,
    ) => {
      const value = quantityUsage ? feeValue * quantityUsage : feeValue;

      return formatCurrency(status !== EPaymentStatus.Unpaid ? 0 : value);
    },
    [],
  );

  const totalDue = useMemo(
    (): number => {
      const {
        soldLots    = [],
        invoiceFees = [],
      } = values;

      const feesTotal = invoiceFees.reduce(
        (acc: number, fee: IInvoiceFee) => acc + getUnpaidFeeValue(fee),
        0,
      );

      return soldLots.reduce(
        (acc: number, soldLot: ISoldLot) => (soldLot.status === EPaymentStatus.Unpaid
          ? acc + soldLot.fee
          : acc),
        feesTotal,
      );
    },
    [values],
  );

  const feeUseTax = useMemo(
    () => {
      const { invoiceFees = [], soldLots = [] } = values;

      const invoiceFeesSum = invoiceFees.reduce(
        (acc, fee) => acc + (fee.taxable ? getUnpaidFeeValue(fee) : 0),
        0,
      );

      const soldLotsSum = soldLots.reduce(
        (acc, soldLot) => acc + (soldLot.taxable ? soldLot.fee : 0),
        0,
      );

      return (invoiceFeesSum + soldLotsSum) * (invoiceData?.taxValue ?? 0);
    },
    [invoiceData, values],
  );

  const total = useMemo(
    () => totalDue + feeUseTax,
    [feeUseTax, totalDue],
  );

  useEffect(
    () => {
      const {
        invoiceFees = [],
        soldLots    = [],
        status,
      } = values;

      if (total === 0 && status === EInvoiceStatus.Unpaid) {
        setValues({
          status      : EInvoiceStatus.Paid,
          soldLots    : soldLots.map((lot) => ({ ...lot, status: EPaymentStatus.Paid })),
          invoiceFees : invoiceFees.map((fee) => ({ ...fee, status: EPaymentStatus.Paid })),
        });
      }
    },
    [total, setValues, values],
  );

  return (
    <StyledForm>
      <h2>Invoice Information</h2>

      <hr />

      <LineWrapper width={isSmallScreen ? '400px' : '800px'}>
        <span>Institution Name: </span>
        <span>{invoiceData.creditUnion.name ?? ''}</span>
      </LineWrapper>

      <LineWrapper width={isSmallScreen ? '400px' : '800px'}>
        <span>Invoice Number:</span>
        <span>{invoiceData.invoiceNumber ?? 0}</span>
      </LineWrapper>

      <LineWrapper width={isSmallScreen ? '400px' : '800px'}>
        <span>Invoice Period:</span>
        <span>{formatParsedZoneDate(invoiceData.invoiceDate, 'MMMM YYYY')}</span>
      </LineWrapper>

      <LineWrapper width={isSmallScreen ? '400px' : '800px'}>
        <span>Status:</span>
        <Field
          id        = "status"
          name      = "status"
          options   = {invoiceStatusOptions}
          align     = "center"
          component = {Select}
          onChange  = {onChangeInvoiceStatus}
        />
      </LineWrapper>

      <h2>Billing Information</h2>
      <hr />

      {!isSmallScreen && (
        <Header>
          <DescriptionColumn><span>Description</span></DescriptionColumn>
          <ViewButtonWrap />
          <QtyColumn><span>Qty</span></QtyColumn>
          <PriceColumn><span>Price</span></PriceColumn>
          <InfoColumn><span>Status</span></InfoColumn>
          <TotalColumn><span>Total</span></TotalColumn>
          <InfoColumn><span /></InfoColumn>
        </Header>
      )}

      <FieldArray
        name="invoiceFees"
        render={() => (
          <>
            {(values.invoiceFees ?? []).map((fee, index) => {
              const feeName    : string = PRICING_LABELS[FEE_PRICING_KEYS[fee.type] ?? 'monthlyFee'];
              const taxedLabel : string = TAXABLE_FEE_TYPES.includes(fee.type) ? '*taxed' : '';

              return (
                <>
                  {isSmallScreen ? (
                    <>
                      <LineWrapper width="400px" firstSpanWidth="100px">
                        <span>Description:</span>
                        <span style={{ fontWeight: 500 }}>{`${feeName} ${taxedLabel}`}</span>
                      </LineWrapper>

                      {fee.type === EFeeType.NadaBookOutFee && (
                        <LineWrapper width="400px" firstSpanWidth="100px" color="#0063A5">
                          <span>Qty:</span>
                          <div style={{ display: 'flex' }}>
                            <Field
                              id                = {`invoiceFees[${index}].quantityUsage`}
                              name              = {`invoiceFees[${index}].quantityUsage`}
                              width             = "60px"
                              component         = {CurrencyInput}
                              fixedDecimalScale = {false}
                              maxLength         = {8}
                              prefix            = ""
                            />

                            {!!fee.quantityUsage && (
                              <Button
                                color="secondary"
                                variant      = "text"
                                isWithLoader = {false}
                                label        = "View Usage"
                                size         = "small"
                                width        = "100%"
                                onClick      = {openNadaUsageModal}
                              />
                            )}
                          </div>
                        </LineWrapper>
                      )}

                      <LineWrapper width="400px" firstSpanWidth="100px">
                        <span>Price:</span>
                        <span>
                          <Field
                            id        = {`invoiceFees[${index}].value`}
                            name      = {`invoiceFees[${index}].value`}
                            component = {CurrencyInput}
                            maxLength = {8}
                          />
                        </span>
                      </LineWrapper>

                      <LineWrapper width="400px" firstSpanWidth="100px">
                        <span>Status:</span>
                        <span>
                          <Field
                            id        = {`invoiceFees[${index}].status`}
                            name      = {`invoiceFees[${index}].status`}
                            options   = {paymentStatusOptions}
                            align     = "center"
                            component = {Select}
                            onChange  = {(event: ChangeEvent<HTMLInputElement>) =>
                              onChangeInvoiceFeeStatus(event, fee.type)}
                          />
                        </span>
                      </LineWrapper>

                      <LineWrapper width="400px" firstSpanWidth="100px">
                        <span>Total:</span>
                        <span>{getFormatCurrency(fee.status, fee.value, fee.quantityUsage)}</span>
                      </LineWrapper>

                      <div style={{ display: 'flex', justifyContent: 'end' }}>
                        <Button
                          variant = "text"
                          label   = "Delete"
                          size    = "small"
                          endIcon = {<Delete />}
                          onClick = {() => handleResetFee(fee.type)}
                        />
                      </div>
                      <hr />
                    </>
                  ) : (
                    <InfoRow key={fee.type}>
                      <DescriptionColumn>
                        <span>{`${feeName} ${taxedLabel}`}</span>
                      </DescriptionColumn>

                      <ViewButtonWrap>
                        {fee.type === EFeeType.NadaBookOutFee && !!fee.quantityUsage && (
                          <WithoutWidthButton
                            variant = "text"
                            label   = "View Usage"
                            onClick = {openNadaUsageModal}
                          />
                        )}
                      </ViewButtonWrap>

                      <QtyColumn width={60}>
                        {fee.type === EFeeType.NadaBookOutFee && (
                          <Field
                            id                = {`invoiceFees[${index}].quantityUsage`}
                            name              = {`invoiceFees[${index}].quantityUsage`}
                            width             = "60px"
                            component         = {CurrencyInput}
                            fixedDecimalScale = {false}
                            maxLength         = {8}
                            prefix            = ""
                          />
                        )}
                      </QtyColumn>

                      <PriceColumn>
                        <Field
                          id        = {`invoiceFees[${index}].value`}
                          name      = {`invoiceFees[${index}].value`}
                          component = {CurrencyInput}
                          maxLength = {8}
                        />
                      </PriceColumn>

                      <InfoColumn>
                        <Field
                          id        = {`invoiceFees[${index}].status`}
                          name      = {`invoiceFees[${index}].status`}
                          options   = {paymentStatusOptions}
                          align     = "center"
                          component = {Select}
                          onChange  = {(event: ChangeEvent<HTMLInputElement>) =>
                            onChangeInvoiceFeeStatus(event, fee.type)}
                        />
                      </InfoColumn>

                      <TotalColumn>
                        <p>
                          {getFormatCurrency(
                            fee.status,
                            fee.value,
                            fee.quantityUsage,
                          )}
                        </p>
                      </TotalColumn>

                      <InfoColumn>
                        <Button
                          variant = "text"
                          label   = "Delete"
                          size    = "small"
                          endIcon = {<Delete />}
                          onClick = {() => handleResetFee(fee.type)}
                        />
                      </InfoColumn>
                    </InfoRow>
                  )}
                </>
              );
            })}
          </>
        )}
      />

      <FieldArray
        name   = "soldLots"
        render = {(): JSX.Element => (
          <>
            {(values.soldLots || []).map(
              (soldLot: ISoldLot, index: number): JSX.Element => (
                <InfoRow key={soldLot.id}>
                  <DescriptionColumn>
                    <span>{soldLot.vehicleDescription}</span>
                    <FeeDescription>
                      {soldLot?.description ?? ''}
                    </FeeDescription>
                  </DescriptionColumn>

                  <QtyColumn />
                  <ViewButtonWrap />

                  <PriceColumn>
                    <Field
                      id        = {`soldLots[${index}].fee`}
                      name      = {`soldLots[${index}].fee`}
                      component = {CurrencyInput}
                      maxLength = {12}
                    />
                  </PriceColumn>

                  <InfoColumn>
                    <Field
                      id        = {`soldLots[${index}].status`}
                      name      = {`soldLots[${index}].status`}
                      align     = "center"
                      component = {Select}
                      options   = {paymentStatusOptions}
                      onChange  = {(event: ChangeEvent<HTMLInputElement>) =>
                        onChangeSoldLotStatus(event, soldLot.id)}
                    />
                  </InfoColumn>

                  <TotalColumn>
                    {getFormatCurrency(
                      soldLot.status,
                      soldLot.fee,
                    )}
                  </TotalColumn>

                  <InfoColumn>
                    <Button
                      variant = "text"
                      label   = "Delete"
                      size    = "small"
                      margin  = "0"
                      endIcon = {<Delete />}
                      onClick = {(): void => handleDeleteSoldLot(soldLot.id)}
                    />
                  </InfoColumn>
                </InfoRow>
              ),
            )}

            <Footer>
              <DescriptionColumn />
              <QtyColumn />
              <ViewButtonWrap />
              <PriceColumn />
              <InfoColumn>
                <Button
                  type      = "submit"
                  width     = "220px"
                  margin    = "0"
                  label     = "Save Changes"
                  isLoading = {isSubmitting}
                  disabled  = {!isValid || !dirty || isSubmitting}
                  fullWidth
                />
              </InfoColumn>
              <InfoColumn />
              <InfoColumn />
            </Footer>
          </>
        )}
      />

      <Footer>
        <DescriptionColumn />
        <PriceColumn />
        <InfoColumn>
          <TotalCount>FEES USE TAX:</TotalCount>
        </InfoColumn>
        <TotalColumn>
          {getFormatCurrency(
            EPaymentStatus.Unpaid,
            feeUseTax,
          )}
        </TotalColumn>
      </Footer>

      <Footer>
        <DescriptionColumn />
        <PriceColumn />
        <InfoColumn>
          <TotalCount>TOTAL DUE:</TotalCount>
        </InfoColumn>
        <TotalColumn>
          {formatCurrency(total)}
        </TotalColumn>
      </Footer>
    </StyledForm>
  );
};

export const InvoiceDetailsForm = memo(withWidth()(InvoiceDetails));
